import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    ButtonGroup,
    Checkbox,
    Chip,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    FormLabel,
    Input,
    Modal,
    ModalDialog,
    Option,
    Radio,
    RadioGroup,
    Select,
    Stack,
    Tooltip,
    Typography,
} from '@mui/joy';
import { CloudUpload, ExpandLess, ExpandMore } from '@mui/icons-material';
import { PROJECT_OPTIONS } from '../../utils/projectDisplayName';

const ENABLED_SOURCES = [
    { id: 'logs', label: 'Logs' },
    { id: 'resource_utilization', label: 'Resource utilization' },
    { id: 'ai_telemetry', label: 'AI telemetry' },
];

const LOG_LEVELS = ['trace', 'debug', 'info', 'notice', 'warning', 'error', 'critical'];
const MAX_RANGE_MS = 30 * 24 * 60 * 60 * 1000;
const MAX_DURATION_MS = 24 * 60 * 60 * 1000;

const DURATION_PRESETS = [
    { id: '15m', label: '15m', ms: 15 * 60 * 1000 },
    { id: '1h', label: '1h', ms: 60 * 60 * 1000 },
    { id: '3h', label: '3h', ms: 3 * 60 * 60 * 1000 },
    { id: '6h', label: '6h', ms: 6 * 60 * 60 * 1000 },
    { id: 'custom', label: 'Custom', ms: null },
];

function pad2(value) {
    return String(value).padStart(2, '0');
}

export function toDatetimeLocalValue(ms) {
    if (ms == null || Number.isNaN(Number(ms))) return '';
    const date = new Date(Number(ms));
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

export function fromDatetimeLocalValue(value) {
    if (!value) return null;
    const ms = new Date(value).getTime();
    return Number.isNaN(ms) ? null : ms;
}

function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}

function rangePickerBounds({ nowMs, fromMs, toMs }) {
    const earliestMs = nowMs - MAX_RANGE_MS;
    const latestMs = nowMs;
    const fromMinMs = toMs != null
        ? Math.max(earliestMs, toMs - MAX_RANGE_MS)
        : earliestMs;
    const fromMaxMs = toMs != null ? Math.min(latestMs, toMs) : latestMs;
    const toMinMs = fromMs != null ? Math.max(earliestMs, fromMs) : earliestMs;
    const toMaxMs = fromMs != null
        ? Math.min(latestMs, fromMs + MAX_RANGE_MS)
        : latestMs;
    return {
        earliestMs,
        latestMs,
        fromMin: toDatetimeLocalValue(fromMinMs),
        fromMax: toDatetimeLocalValue(Math.max(fromMinMs, fromMaxMs)),
        toMin: toDatetimeLocalValue(toMinMs),
        toMax: toDatetimeLocalValue(Math.max(toMinMs, toMaxMs)),
    };
}

function defaultSources(initialSources) {
    if (Array.isArray(initialSources) && initialSources.length > 0) {
        return {
            logs: initialSources.includes('logs'),
            resource_utilization: initialSources.includes('resource_utilization'),
            ai_telemetry: initialSources.includes('ai_telemetry'),
        };
    }
    return { logs: true, resource_utilization: true, ai_telemetry: true };
}

function parseTagTokens(text) {
    return text
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean);
}

function resolveDurationMs(presetId, customAmount, customUnit) {
    const preset = DURATION_PRESETS.find((item) => item.id === presetId);
    if (preset && preset.ms != null) return preset.ms;
    const amount = Number(customAmount);
    if (!Number.isFinite(amount) || amount <= 0) return 0;
    return amount * (customUnit === 'hours' ? 60 * 60 * 1000 : 60 * 1000);
}

export default function UploadRequestDialog({
    open,
    onClose,
    onSubmit,
    submitting = false,
    initialFromMs,
    initialToMs,
    initialSources,
    initialMode = 'range_upload',
    sessionActive = false,
}) {
    const [mode, setMode] = useState(initialMode);
    const [sources, setSources] = useState(() => defaultSources(initialSources));
    const [fromLocal, setFromLocal] = useState('');
    const [toLocal, setToLocal] = useState('');
    const [boundsNowMs, setBoundsNowMs] = useState(() => Date.now());
    const [durationPreset, setDurationPreset] = useState('1h');
    const [customAmount, setCustomAmount] = useState('1');
    const [customUnit, setCustomUnit] = useState('hours');
    const [captureLevel, setCaptureLevel] = useState('debug');
    const [logFiltersOpen, setLogFiltersOpen] = useState(false);
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [projects, setProjects] = useState([]);
    const [tags, setTags] = useState([]);
    const [tagDraft, setTagDraft] = useState('');
    const [minLevel, setMinLevel] = useState('');
    const [forceResync, setForceResync] = useState(false);
    const [validationError, setValidationError] = useState(null);
    const [submitError, setSubmitError] = useState(null);

    useEffect(() => {
        if (!open) return;
        const now = Date.now();
        setBoundsNowMs(now);
        const earliestMs = now - MAX_RANGE_MS;
        const fromMs = clamp(initialFromMs ?? now - 60 * 60 * 1000, earliestMs, now);
        const toMs = clamp(initialToMs ?? now, fromMs, now);
        setMode(initialMode === 'collection_session' ? 'collection_session' : 'range_upload');
        setSources(defaultSources(initialSources));
        setFromLocal(toDatetimeLocalValue(fromMs));
        setToLocal(toDatetimeLocalValue(toMs));
        setDurationPreset('1h');
        setCustomAmount('1');
        setCustomUnit('hours');
        setCaptureLevel('debug');
        setLogFiltersOpen(false);
        setAdvancedOpen(false);
        setProjects([]);
        setTags([]);
        setTagDraft('');
        setMinLevel('');
        setForceResync(false);
        setValidationError(null);
        setSubmitError(null);
    }, [open, initialFromMs, initialToMs, initialSources, initialMode]);

    const selectedSources = useMemo(
        () => ENABLED_SOURCES.filter((source) => sources[source.id]).map((source) => source.id),
        [sources]
    );

    const pickerBounds = useMemo(
        () => rangePickerBounds({
            nowMs: boundsNowMs,
            fromMs: fromDatetimeLocalValue(fromLocal),
            toMs: fromDatetimeLocalValue(toLocal),
        }),
        [boundsNowMs, fromLocal, toLocal]
    );
    const isCollect = mode === 'collection_session';
    const collectSubmitBlocked = isCollect && sessionActive;
    const durationMs = resolveDurationMs(durationPreset, customAmount, customUnit);

    const commitTagDraft = () => {
        const next = parseTagTokens(tagDraft);
        if (next.length === 0) return;
        setTags((prev) => Array.from(new Set([...prev, ...next])));
        setTagDraft('');
    };

    const validate = () => {
        if (selectedSources.length === 0) {
            return 'Select at least one source.';
        }
        if (isCollect) {
            if (!(durationMs > 0)) {
                return 'Duration must be greater than 0.';
            }
            if (durationMs > MAX_DURATION_MS) {
                return 'Duration must be 24 hours or less.';
            }
            return null;
        }
        const fromMs = fromDatetimeLocalValue(fromLocal);
        const toMs = fromDatetimeLocalValue(toLocal);
        if (fromMs == null || toMs == null) {
            return 'From and To are required.';
        }
        if (!(fromMs < toMs)) {
            return 'From must be before To.';
        }
        if (toMs - fromMs > MAX_RANGE_MS) {
            return 'Range must be 30 days or less.';
        }
        if (fromMs < pickerBounds.earliestMs) {
            return 'From cannot be more than 30 days ago.';
        }
        if (toMs > pickerBounds.latestMs) {
            return 'To cannot be in the future.';
        }
        if (fromMs > Date.now()) {
            return 'Range cannot be entirely in the future.';
        }
        return null;
    };

    const buildLogsFilters = () => {
        if (!sources.logs) return undefined;
        const logs = {};
        if (projects.length > 0) logs.projects = projects;
        if (tags.length > 0) logs.tags = tags;
        if (minLevel) logs.min_level = minLevel;
        if (isCollect && captureLevel) logs.capture_level = captureLevel;
        if (Object.keys(logs).length === 0) return undefined;
        return { logs };
    };

    const buildBody = () => {
        if (isCollect) {
            const body = {
                type: 'collection_session',
                sources: selectedSources,
                duration_ms: durationMs,
            };
            const filters = buildLogsFilters();
            if (filters) body.filters = filters;
            return body;
        }

        const body = {
            type: 'range_upload',
            sources: selectedSources,
            from_ms: fromDatetimeLocalValue(fromLocal),
            to_ms: fromDatetimeLocalValue(toLocal),
            force_resync: Boolean(forceResync),
        };

        const filters = buildLogsFilters();
        if (filters) body.filters = filters;

        return body;
    };

    const handleSubmit = async () => {
        if (collectSubmitBlocked) {
            setValidationError('A collection session is already running for this hub.');
            setSubmitError(null);
            return;
        }
        const clientError = validate();
        if (clientError) {
            setValidationError(clientError);
            setSubmitError(null);
            return;
        }
        setValidationError(null);
        setSubmitError(null);

        const result = await onSubmit(buildBody());
        if (result?.ok) {
            onClose();
            return;
        }
        setSubmitError(result?.error || 'Request failed.');
    };

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog size="lg" sx={{ maxWidth: 640, maxHeight: '90vh', overflow: 'auto' }}>
                <DialogTitle>Request hub data</DialogTitle>
                <DialogContent>
                    <Stack spacing={2.5} sx={{ mt: 1 }}>
                        <FormControl>
                            <FormLabel>Mode</FormLabel>
                            <RadioGroup
                                value={mode}
                                onChange={(event) => {
                                    setMode(event.target.value);
                                    setValidationError(null);
                                    setSubmitError(null);
                                }}
                            >
                                <Radio value="range_upload" label="Upload existing" />
                                <Radio value="collection_session" label="Collect then upload" />
                            </RadioGroup>
                        </FormControl>

                        <FormControl>
                            <FormLabel>Sources</FormLabel>
                            <Stack spacing={1}>
                                {ENABLED_SOURCES.map((source) => (
                                    <Checkbox
                                        key={source.id}
                                        label={source.label}
                                        checked={Boolean(sources[source.id])}
                                        onChange={(event) => {
                                            setSources((prev) => ({
                                                ...prev,
                                                [source.id]: event.target.checked,
                                            }));
                                        }}
                                    />
                                ))}
                                <Tooltip title="uploaded automatically (Tier 0)" placement="right">
                                    <span>
                                        <Checkbox label="Events" checked={false} disabled />
                                    </span>
                                </Tooltip>
                            </Stack>
                        </FormControl>

                        {isCollect ? (
                            <Stack spacing={1.5}>
                                <FormControl>
                                    <FormLabel>Duration</FormLabel>
                                    <ButtonGroup size="sm">
                                        {DURATION_PRESETS.map((preset) => (
                                            <Button
                                                key={preset.id}
                                                variant={durationPreset === preset.id ? 'solid' : 'outlined'}
                                                onClick={() => {
                                                    if (preset.id === 'custom' && durationPreset !== 'custom') {
                                                        const current = DURATION_PRESETS.find((item) => item.id === durationPreset);
                                                        if (current?.ms) {
                                                            if (current.ms % (60 * 60 * 1000) === 0) {
                                                                setCustomAmount(String(current.ms / (60 * 60 * 1000)));
                                                                setCustomUnit('hours');
                                                            } else {
                                                                setCustomAmount(String(current.ms / (60 * 1000)));
                                                                setCustomUnit('minutes');
                                                            }
                                                        }
                                                    }
                                                    setDurationPreset(preset.id);
                                                }}
                                            >
                                                {preset.label}
                                            </Button>
                                        ))}
                                    </ButtonGroup>
                                </FormControl>
                                {durationPreset === 'custom' && (
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                                        <FormControl sx={{ flex: 1 }}>
                                            <FormLabel>Custom duration</FormLabel>
                                            <Input
                                                type="number"
                                                slotProps={{ input: { min: 0, step: 'any' } }}
                                                value={customAmount}
                                                onChange={(event) => setCustomAmount(event.target.value)}
                                            />
                                        </FormControl>
                                        <FormControl sx={{ minWidth: 140 }}>
                                            <FormLabel>Unit</FormLabel>
                                            <Select
                                                value={customUnit}
                                                onChange={(_, value) => {
                                                    if (value) setCustomUnit(value);
                                                }}
                                            >
                                                <Option value="minutes">minutes</Option>
                                                <Option value="hours">hours</Option>
                                            </Select>
                                        </FormControl>
                                    </Stack>
                                )}
                                <Typography level="body-xs" textColor="neutral.500">
                                    Starts immediately. Maximum 24 hours.
                                </Typography>
                                <Alert color="warning" variant="soft">
                                    Log verbosity will change for this duration. Manual log-level edits are blocked until the session ends.
                                </Alert>
                                {sources.logs && (
                                    <FormControl>
                                        <FormLabel>Capture log level</FormLabel>
                                        <Select
                                            value={captureLevel}
                                            onChange={(_, value) => setCaptureLevel(value || 'debug')}
                                        >
                                            {LOG_LEVELS.map((level) => (
                                                <Option key={level} value={level}>
                                                    {level}
                                                </Option>
                                            ))}
                                        </Select>
                                        <FormHelperText>
                                            Temporary hub log level while the session is collecting.
                                        </FormHelperText>
                                    </FormControl>
                                )}
                            </Stack>
                        ) : (
                            <Stack spacing={1}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    <FormControl sx={{ flex: 1 }}>
                                        <FormLabel>From</FormLabel>
                                        <Input
                                            type="datetime-local"
                                            value={fromLocal}
                                            slotProps={{
                                                input: {
                                                    min: pickerBounds.fromMin,
                                                    max: pickerBounds.fromMax,
                                                },
                                            }}
                                            onChange={(event) => setFromLocal(event.target.value)}
                                        />
                                    </FormControl>
                                    <FormControl sx={{ flex: 1 }}>
                                        <FormLabel>To</FormLabel>
                                        <Input
                                            type="datetime-local"
                                            value={toLocal}
                                            slotProps={{
                                                input: {
                                                    min: pickerBounds.toMin,
                                                    max: pickerBounds.toMax,
                                                },
                                            }}
                                            onChange={(event) => setToLocal(event.target.value)}
                                        />
                                    </FormControl>
                                </Stack>
                                <Typography level="body-xs" textColor="neutral.500">
                                    Only the last 30 days are selectable.
                                </Typography>
                            </Stack>
                        )}

                        {sources.logs && (
                            <Box>
                                <Button
                                    size="sm"
                                    variant="plain"
                                    color="neutral"
                                    endDecorator={logFiltersOpen ? <ExpandLess /> : <ExpandMore />}
                                    onClick={() => setLogFiltersOpen((prev) => !prev)}
                                >
                                    Log filters
                                </Button>
                                {logFiltersOpen && (
                                    <CardLike>
                                        <Stack spacing={1.5}>
                                            {isCollect && (
                                                <Typography level="body-xs" textColor="neutral.500">
                                                    Applied when collected data is uploaded.
                                                </Typography>
                                            )}
                                            <FormControl>
                                                <FormLabel>Projects</FormLabel>
                                                <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                                                    {PROJECT_OPTIONS.map((project) => (
                                                        <Checkbox
                                                            key={project.value}
                                                            size="sm"
                                                            label={`${project.label} (${project.value})`}
                                                            checked={projects.includes(project.value)}
                                                            onChange={(event) => {
                                                                setProjects((prev) => (
                                                                    event.target.checked
                                                                        ? [...prev, project.value]
                                                                        : prev.filter((id) => id !== project.value)
                                                                ));
                                                            }}
                                                        />
                                                    ))}
                                                </Stack>
                                            </FormControl>

                                            <FormControl>
                                                <FormLabel>Tags</FormLabel>
                                                <Input
                                                    placeholder="Comma-separated, then Enter"
                                                    value={tagDraft}
                                                    onChange={(event) => setTagDraft(event.target.value)}
                                                    onBlur={commitTagDraft}
                                                    onKeyDown={(event) => {
                                                        if (event.key === 'Enter' || event.key === ',') {
                                                            event.preventDefault();
                                                            commitTagDraft();
                                                        }
                                                    }}
                                                />
                                                {tags.length > 0 && (
                                                    <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5} sx={{ mt: 1 }}>
                                                        {tags.map((tag) => (
                                                            <Chip
                                                                key={tag}
                                                                size="sm"
                                                                variant="soft"
                                                                onClick={() => setTags((prev) => prev.filter((item) => item !== tag))}
                                                            >
                                                                {tag} ×
                                                            </Chip>
                                                        ))}
                                                    </Stack>
                                                )}
                                            </FormControl>

                                            <FormControl>
                                                <FormLabel>Minimum level</FormLabel>
                                                <Select
                                                    value={minLevel || null}
                                                    placeholder="Any"
                                                    onChange={(_, value) => setMinLevel(value || '')}
                                                >
                                                    {LOG_LEVELS.map((level) => (
                                                        <Option key={level} value={level}>
                                                            {level}
                                                        </Option>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Stack>
                                    </CardLike>
                                )}
                            </Box>
                        )}

                        {!isCollect && (
                            <Box>
                                <Button
                                    size="sm"
                                    variant="plain"
                                    color="neutral"
                                    endDecorator={advancedOpen ? <ExpandLess /> : <ExpandMore />}
                                    onClick={() => setAdvancedOpen((prev) => !prev)}
                                >
                                    Advanced
                                </Button>
                                {advancedOpen && (
                                    <CardLike>
                                        <Checkbox
                                            label="Force resync"
                                            checked={forceResync}
                                            onChange={(event) => setForceResync(event.target.checked)}
                                        />
                                        <Alert color="warning" variant="soft" sx={{ mt: 1 }}>
                                            Re-sends already uploaded rows.
                                        </Alert>
                                    </CardLike>
                                )}
                            </Box>
                        )}

                        {collectSubmitBlocked && (
                            <Alert color="warning" variant="soft">
                                A collection session is already running for this hub.
                            </Alert>
                        )}

                        {(validationError || submitError) && (
                            <Alert color="danger" variant="soft">
                                {submitError || validationError}
                            </Alert>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button variant="plain" onClick={onClose} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button
                        variant="solid"
                        loading={submitting}
                        disabled={collectSubmitBlocked}
                        startDecorator={<CloudUpload />}
                        onClick={handleSubmit}
                    >
                        Submit
                    </Button>
                </DialogActions>
            </ModalDialog>
        </Modal>
    );
}

function CardLike({ children }) {
    return (
        <Box
            sx={{
                mt: 1,
                p: 1.5,
                borderRadius: 'sm',
                bgcolor: 'background.level1',
                border: '1px solid',
                borderColor: 'neutral.outlinedBorder',
            }}
        >
            {children}
        </Box>
    );
}
